import type { RedoAdminRpcClient } from "@redotech/admin-sdk/rpc/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import EyeIcon from "@redotech/redo-icons/eye.svg";
import PlayIcon from "@redotech/redo-icons/play.svg";
import StopIcon from "@redotech/redo-icons/stop-circle.svg";
import { toast } from "@redotech/redo-web/alert";
import {
  RedoBadge,
  type RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  memo,
  useState,
  type JSXElementConstructor,
  type ReactNode,
} from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import {
  RECONCILE_JOB_SUMMARY,
  RECONCILE_OPTIONS_BY_KEY,
} from "./support-v2-maintenance-options";

type ReconcileStatus = Awaited<
  ReturnType<RedoAdminRpcClient["getSupportV2ReconcileResult"]>
>["status"];

type ReconcileResult = NonNullable<
  Awaited<
    ReturnType<RedoAdminRpcClient["getSupportV2ReconcileResult"]>
  >["result"]
>;

const RESULT_POLL_INTERVAL_MS = 3000;
const numberFormatter = new Intl.NumberFormat();
const formatCount = (value: number) => numberFormatter.format(value);

const formatError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const STATUS_BADGE: Record<
  ReconcileStatus,
  {
    color: RedoBadgeProps["color"];
    Icon?: JSXElementConstructor<unknown>;
    text: string;
  }
> = {
  cancelled: { color: "gray", Icon: StopIcon, text: "Cancelled" },
  completed: { color: "success", Icon: CheckCircleIcon, text: "Completed" },
  failed: { color: "error", Icon: AlertTriangleIcon, text: "Failed" },
  running: { color: "blue", Icon: ClockIcon, text: "Running" },
  unknown: { color: "gray", text: "Unknown" },
};

const StatItem = ({ label, value }: { label: string; value: number }) => (
  <Flex align="center" gap="md" justify="space-between" w="full">
    <Text fontSize="sm" textColor="secondary">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {formatCount(value)}
    </Text>
  </Flex>
);

const StatGroup = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Flex dir="column" gap="xs">
    <Text fontSize="xs" fontWeight="medium" textColor="secondary">
      {title}
    </Text>
    {children}
  </Flex>
);

const ReconcileResultView = memo(function ReconcileResultView({
  result,
}: {
  result: ReconcileResult;
}) {
  return (
    <Flex dir="column" gap="md">
      <RedoBadge
        color={result.dryRun ? "gray" : "brand"}
        size="sm"
        text={result.dryRun ? "Preview plan" : "Applied"}
      />
      <StatGroup title="Plan">
        <StatItem label="Candidates scanned" value={result.candidates} />
        <StatItem label="Total changes" value={result.total} />
      </StatGroup>
      <StatGroup title={result.dryRun ? "Would change" : "Changed"}>
        <StatItem label="Duplicates deleted" value={result.deleted} />
        <StatItem label="Orphans closed" value={result.orphansClosed} />
        <StatItem label="Closed (absent in v1)" value={result.v2OnlyClosed} />
        <StatItem label="Legacy status synced" value={result.legacyChanged} />
        <StatItem label="Open duplicates closed" value={result.dedupeClosed} />
      </StatGroup>
    </Flex>
  );
});

export const ReconcileSupportV2Modal = memo(function ReconcileSupportV2Modal({
  teamId,
  teamLabel,
  onClose,
}: {
  teamId: string;
  teamLabel?: string;
  onClose: () => void;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [closeV2Only, setCloseV2Only] = useState(false);
  const [dedupeOpen, setDedupeOpen] = useState(false);
  const [matchVoice, setMatchVoice] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [activeRun, setActiveRun] = useState<{
    workflowId: string;
    dryRun: boolean;
  } | null>(null);

  const runMutation = useMutation({
    mutationFn: (dryRun: boolean) =>
      rpcClient.runSupportV2Reconcile({
        teamId,
        dryRun,
        ...(closeV2Only ? { closeV2Only: true } : {}),
        ...(dedupeOpen ? { dedupeOpen: true } : {}),
        ...(matchVoice ? { matchVoice: true } : {}),
      }),
    onSuccess: (result, dryRun) => {
      setActiveRun({ workflowId: result.workflowId, dryRun });
      setConfirmApply(false);
    },
    onError: (error) => {
      toast(formatError(error), { variant: "error" });
    },
  });

  const resultQuery = useQuery({
    enabled: activeRun !== null,
    queryFn: () => {
      if (!activeRun) {
        throw new Error("No active reconcile run");
      }
      return rpcClient.getSupportV2ReconcileResult({
        workflowId: activeRun.workflowId,
      });
    },
    queryKey: ["supportV2ReconcileResult", activeRun?.workflowId] as const,
    refetchInterval: (query) =>
      query.state.data?.status === "running" ? RESULT_POLL_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
  });

  const runResult = resultQuery.data;
  const status: ReconcileStatus | undefined = runResult?.status;
  const statusConfig = status ? STATUS_BADGE[status] : undefined;

  const closeRequested = useHandler(() => {
    if (!runMutation.isPending) {
      onClose();
    }
  });

  const runPreview = useHandler(() => {
    runMutation.mutate(true);
  });

  const runApply = useHandler(() => {
    runMutation.mutate(false);
  });

  const primaryButton = confirmApply
    ? {
        IconLeading: PlayIcon,
        onClick: runApply,
        pending: runMutation.isPending,
        text: "Apply changes",
        theme: "destructive" as const,
      }
    : {
        IconLeading: EyeIcon,
        onClick: runPreview,
        pending: runMutation.isPending,
        text: "Run preview",
      };

  const secondaryButton = confirmApply
    ? {
        disabled: runMutation.isPending,
        onClick: () => setConfirmApply(false),
        text: "Back",
      }
    : {
        disabled: runMutation.isPending,
        IconLeading: PlayIcon,
        onClick: () => setConfirmApply(true),
        text: "Apply…",
        theme: "destructive" as const,
      };

  return (
    <RedoModal
      headerIconAlignment="inline"
      modalSize="md"
      onModalCloseRequested={closeRequested}
      primaryButton={primaryButton}
      secondaryButton={secondaryButton}
      subtitle={
        teamLabel
          ? `Reconcile support-v2 tickets to v1 for ${teamLabel}.`
          : "Reconcile support-v2 tickets to v1."
      }
      theme={confirmApply ? "error" : "none"}
      title="Reconcile support-v2 tickets"
    >
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          {RECONCILE_JOB_SUMMARY}
        </Text>

        {confirmApply && (
          <Text fontSize="sm" fontWeight="medium" textColor="error">
            Apply writes to support-v2: it soft-deletes duplicate tickets and
            closes/archives tickets to match v1. It never reopens. Run a preview
            first if unsure.
          </Text>
        )}

        <RedoCollapseSubsection defaultCollapsed={false} title="Options">
          <Flex dir="column" gap="md" pl="lg">
            <Flex dir="column" gap="xxs">
              <RedoCheckbox
                description={RECONCILE_OPTIONS_BY_KEY.closeV2Only.description}
                label={RECONCILE_OPTIONS_BY_KEY.closeV2Only.label}
                setValue={(value) => setCloseV2Only(value === true)}
                size="sm"
                value={closeV2Only}
              />
              {RECONCILE_OPTIONS_BY_KEY.closeV2Only.riskNote && (
                <Text fontSize="xs" pl="3xl" textColor="warning">
                  {RECONCILE_OPTIONS_BY_KEY.closeV2Only.riskNote}
                </Text>
              )}
            </Flex>
            <Flex dir="column" gap="xxs">
              <RedoCheckbox
                description={RECONCILE_OPTIONS_BY_KEY.dedupeOpen.description}
                label={RECONCILE_OPTIONS_BY_KEY.dedupeOpen.label}
                setValue={(value) => setDedupeOpen(value === true)}
                size="sm"
                value={dedupeOpen}
              />
              {RECONCILE_OPTIONS_BY_KEY.dedupeOpen.riskNote && (
                <Text fontSize="xs" pl="3xl" textColor="warning">
                  {RECONCILE_OPTIONS_BY_KEY.dedupeOpen.riskNote}
                </Text>
              )}
            </Flex>
            <Flex dir="column" gap="xxs">
              <RedoCheckbox
                description={RECONCILE_OPTIONS_BY_KEY.matchVoice.description}
                label={RECONCILE_OPTIONS_BY_KEY.matchVoice.label}
                setValue={(value) => setMatchVoice(value === true)}
                size="sm"
                value={matchVoice}
              />
              {RECONCILE_OPTIONS_BY_KEY.matchVoice.riskNote && (
                <Text fontSize="xs" pl="3xl" textColor="warning">
                  {RECONCILE_OPTIONS_BY_KEY.matchVoice.riskNote}
                </Text>
              )}
            </Flex>
          </Flex>
        </RedoCollapseSubsection>

        {runMutation.error && (
          <Text fontSize="sm" textColor="error">
            {formatError(runMutation.error)}
          </Text>
        )}

        {activeRun && (
          <>
            <Divider />
            <Flex dir="column" gap="md">
              <Flex align="center" gap="sm" justify="space-between" wrap="wrap">
                <Text fontSize="sm" fontWeight="medium">
                  {activeRun.dryRun ? "Preview run" : "Apply run"}
                </Text>
                {statusConfig && (
                  <RedoBadge
                    color={statusConfig.color}
                    segmentLeading={
                      statusConfig.Icon
                        ? { type: "icon", Icon: statusConfig.Icon, size: "sm" }
                        : undefined
                    }
                    size="sm"
                    text={statusConfig.text}
                  />
                )}
              </Flex>
              <Text
                fontFamily="monospace"
                fontSize="xs"
                overflow="hidden"
                textColor="secondary"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {activeRun.workflowId}
              </Text>

              {resultQuery.error && (
                <Text fontSize="sm" textColor="error">
                  {formatError(resultQuery.error)}
                </Text>
              )}
              {status === "running" && (
                <Text fontSize="sm" textColor="secondary">
                  Job running… results appear when it completes.
                </Text>
              )}
              {runResult?.error && (
                <Text fontSize="sm" textColor="error">
                  {runResult.error}
                </Text>
              )}
              {runResult?.result && (
                <ReconcileResultView result={runResult.result} />
              )}
            </Flex>
          </>
        )}
      </Flex>
    </RedoModal>
  );
});
